import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Mail, 
  MapPin, 
  Send, 
  CheckCircle,
  ArrowRight
} from 'lucide-react';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section 
        data-testid="contact-hero"
        className="relative pt-28 pb-16 bg-slate-50"
      >
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-blue-600 font-medium text-sm tracking-wide uppercase mb-3">
              Get In Touch
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-5">
              Contact Us
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Have questions, ideas, or want to get involved? We'd love to hear from you. 
              Reach out and let's start a conversation.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section data-testid="contact-form-section" className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28 space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-3">
                    Contact Information
                  </h2>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Reach out through the form or contact us directly. 
                    We typically respond within 1-2 business days.
                  </p>
                </div>

                <div className="space-y-3">
                  <a
                    href="mailto:info@hagen-family-foundation.com"
                    data-testid="contact-email"
                    className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-700" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="text-slate-900 text-sm font-medium">info@hagen-family-foundation.com</p>
                    </div>
                  </a>

                  <div 
                    data-testid="contact-location"
                    className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-lg"
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-blue-700" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Location</p>
                      <p className="text-slate-900 text-sm font-medium">Tacloban City, Philippines</p>
                    </div>
                  </div>
                </div>

                <div className="pt-5 border-t border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-3 text-sm">
                    Quick Links
                  </h3>
                  <div className="space-y-2">
                    <Link
                      to="/mission"
                      className="flex items-center gap-2 text-blue-700 hover:text-blue-800 text-sm font-medium transition-colors"
                    >
                      Our Mission <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link
                      to="/initiatives"
                      className="flex items-center gap-2 text-blue-700 hover:text-blue-800 text-sm font-medium transition-colors"
                    >
                      Our Initiatives <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link
                      to="/calendar"
                      className="flex items-center gap-2 text-blue-700 hover:text-blue-800 text-sm font-medium transition-colors"
                    >
                      Upcoming Events <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              {isSubmitted ? (
                <div 
                  data-testid="contact-success"
                  className="bg-green-50 border border-green-200 rounded-lg p-10 text-center"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">
                    Thank You!
                  </h3>
                  <p className="text-slate-600 mb-6">
                    We've received your message and will be in touch soon. 
                    We typically respond within 1-2 business days.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    data-testid="send-another-message"
                    className="inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-md px-5 py-2.5 font-medium transition-colors"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1.5">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        data-testid="contact-name"
                        className={`w-full bg-white border ${errors.name ? 'border-red-400' : 'border-slate-300'} focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 rounded-md px-4 py-2.5 text-slate-900 placeholder:text-slate-400 transition-colors outline-none`}
                        placeholder="John Doe"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        data-testid="contact-email-input"
                        className={`w-full bg-white border ${errors.email ? 'border-red-400' : 'border-slate-300'} focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 rounded-md px-4 py-2.5 text-slate-900 placeholder:text-slate-400 transition-colors outline-none`}
                        placeholder="john@example.com"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-1.5">
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      data-testid="contact-subject"
                      className={`w-full bg-white border ${errors.subject ? 'border-red-400' : 'border-slate-300'} focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 rounded-md px-4 py-2.5 text-slate-900 placeholder:text-slate-400 transition-colors outline-none`}
                      placeholder="How can we help?"
                    />
                    {errors.subject && (
                      <p className="text-red-500 text-xs mt-1">{errors.subject}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-1.5">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      data-testid="contact-message"
                      className={`w-full bg-white border ${errors.message ? 'border-red-400' : 'border-slate-300'} focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 rounded-md px-4 py-2.5 text-slate-900 placeholder:text-slate-400 transition-colors outline-none resize-none`}
                      placeholder="Tell us more about how we can help..."
                    />
                    {errors.message && (
                      <p className="text-red-500 text-xs mt-1">{errors.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit"
                    className="inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-md px-6 py-3 font-medium transition-colors shadow-sm"
                  >
                    <Send className="w-4 h-4" />
                    Send Message
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section data-testid="contact-faq" className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-3">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600">
              Quick answers to common questions about the Hagen-Family-Foundation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <h3 className="font-semibold text-slate-900 mb-2 text-sm">
                How can I support the foundation?
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                You can support us through donations, partnerships, volunteering, 
                or simply spreading awareness about our mission.
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <h3 className="font-semibold text-slate-900 mb-2 text-sm">
                How do I apply for a scholarship?
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Scholarship applications will be announced on our calendar page. 
                Subscribe to our newsletter to stay informed about deadlines.
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <h3 className="font-semibold text-slate-900 mb-2 text-sm">
                Is the foundation a 501(c)(3)?
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                We are currently in the process of establishing our 501(c)(3) 
                nonprofit status. Stay tuned for updates!
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <h3 className="font-semibold text-slate-900 mb-2 text-sm">
                Where is the foundation based?
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Our educational programs focus on Tacloban City, Philippines, 
                with plans to expand throughout the country.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
